import { useCallback, useEffect, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'

import { Header, PageTitle } from '@/components'
import { Button, Card, Checkbox } from '@/components/ui'
import Loader from '../../../common/components/Loader'
import { IColumn, Table } from '../../../core/components/Table'
import { formatDateTime } from '../../../core/constants/times'
import { useTableParams } from '../../../core/hooks/table-params'
import {
  DonwloadInvitesPaginationParams,
  IInviteBatch,
  cancelInviteBatch,
  getAllDownloadInvites,
} from '../../../core/services/invites'
import { useAppSelector } from '../../../store'
import { DeleteConfirmationModal } from '../DeleteConfirmationModal'
import { MessageBox } from '../messages/MessageBox'
import { Pagination } from '../Pagination'

export const InvitesDownloadsPage: React.FC = () => {
  const {
    page,
    setPage,
    size,
    setSize,
    totalElements,
    setTotalElemenets,
    sortBy,
    setSortBy,
  } = useTableParams({
    defaultSortBy: 'updatedAt',
  })

  const [item, setItem] = useState<IInviteBatch | undefined>(undefined)

  const { editionId, festivalName } = useParams()
  const { festivals: storeFestivals } = useAppSelector((s) => s.app)
  const festivalFullName = storeFestivals.find(
    (f) => f.platformName === festivalName,
  )?.name
  const timer = useRef<NodeJS.Timeout | null>(null)
  const searchParams = useRef<{ [key: string]: string | null }>({})
  const [sortDirection, setSortDirection] = useState<
    'ASC' | 'DESC' | undefined
  >(undefined)

  const [isError, setIsError] = useState<boolean>(false)
  const [showMessageBox, setShowMessageBox] = useState<boolean>(false)
  const [message, setMessage] = useState<string>('')
  const [showCancelOrderModal, setShowCancelOrderModal] =
    useState<boolean>(false)

  const [invites, setInvites] = useState<IInviteBatch[]>([])
  const [loading, setLoading] = useState<boolean>(false)

  const getData = useCallback(async () => {
    try {
      setLoading(true)
      const { data, status } = await getAllDownloadInvites({
        size,
        page,
        sortBy,
      })
      if (status === 200) {
        setInvites(data.content)
        setTotalElemenets(data.totalElements)
      }
    } catch (e) {
      if (e instanceof Error) {
        setIsError(true)
        setShowMessageBox(true)
        setMessage(e.message)
      } else {
        console.error(e)
      }
    } finally {
      setLoading(false)
    }
  }, [size, page, sortBy, editionId, festivalName, setTotalElemenets])

  useEffect(() => {
    getData()
  }, [getData])

  const handleSortBy = (sort: string) => {
    if (sort === sortBy) {
      if (sortDirection === 'ASC') {
        setSortDirection('DESC')
      } else {
        setSortDirection(undefined)
        setSortBy(undefined)
      }
    } else {
      setSortDirection('ASC')
      setSortBy(sort)
    }
  }

  const pageChange = async (newPage: number) => {
    setPage(newPage)
  }

  const downloadBatch = async (row: IInviteBatch) => {
    window.open(row.zipLink, '_blank')
  }

  const handleSearchParams = useCallback(
    async (col: IColumn, q: string | null) => {
      if (timer.current) {
        clearTimeout(timer.current)
      }

      searchParams.current = {
        ...searchParams.current,
        [col.accessor]: q,
      }

      const newTimer = setTimeout(async () => {
        setLoading(true)
        try {
          const params: DonwloadInvitesPaginationParams = {
            page: 0,
            size,
            sortBy,
            sortDirection,
          }

          params.label = searchParams.current.labelName
          const { data: res, status } = await getAllDownloadInvites(params)
          if (status === 200) {
            setInvites(res.content)
            setTotalElemenets(res.totalElements)
            setSize(res.size)
            setPage(res.pageNumber + 1)
          }
        } catch (err) {
          console.log(err)
        } finally {
          setLoading(false)
        }
      }, 2000)
      timer.current = newTimer
    },
    [page, size, sortBy],
  )

  const columns: IColumn[] = [
    {
      title: 'Label Name',
      accessor: 'labelName',
      sortable: true,
      searchable: true,
      render: (row) => (
        <div className="flex flex-col">
          <span>{row.label}</span>
          <div className="flex gap-2">
            {row.zipLink && (
              <Button
                size="sm"
                variant="link"
                className="p-0"
                onClick={async () => downloadBatch(row)}
              >
                ZIP
              </Button>
            )}
          </div>
        </div>
      ),
    },
    {
      title: 'Ticket',
      accessor: 'ticket',
      render: (row) => (
        <div className="flex flex-col">
          <div className="flex flex-col">
            <div
              key={`${row?.ticket?.category?.name}-${row?.ticket?.ticket?.name}`}
              className="flex gap-2"
            >
              <Checkbox checked disabled />
              <span>{`${row?.ticket?.category?.name}, ${row?.ticket?.ticket?.name}`}</span>
            </div>
          </div>
        </div>
      ),
      width: '30%',
    },
    {
      title: 'Created by',
      accessor: 'createdBy',
      render: (row) => `${row.createdBy}`,
    },
    {
      title: 'Generated Date',
      accessor: 'generatedDate',
      render: (row) =>
        row.createdTimestamp ? (
          <span>{formatDateTime(row.createdTimestamp)}</span>
        ) : (
          '-'
        ),
    },
    {
      title: 'ZIP Password',
      accessor: 'zipPassword',
      sortable: true,
    },
    {
      title: 'Status',
      accessor: 'status',
      sortable: true,
      render: (row: IInviteBatch) => (
        <>
          {row.status === 'COMPLETED' ? (
            <span className="text-green-600">COMPLETED</span>
          ) : row.status === 'PROCESSING' ? (
            <span className="text-gray-600">Processing</span>
          ) : row.status === 'CANCELED' ? (
            <span className="text-red-600">Cancelled</span>
          ) : (
            <span className="text-red-600">Failed</span>
          )}
        </>
      ),
    },
    {
      title: 'Actions',
      accessor: 'actions',
      render: (row: IInviteBatch) =>
        row.status !== 'CANCELED' ? (
          <Button
            size="sm"
            variant="destructive"
            className="whitespace-nowrap"
            onClick={() => {
              setItem(row)
              setShowCancelOrderModal(true)
            }}
          >
            Cancel Order
          </Button>
        ) : (
          '-'
        ),
    },
  ]

  const handleCancelOrder = useCallback(async () => {
    if (item) {
      try {
        setLoading(true)
        const resp = await cancelInviteBatch(item.id)
        if (resp.status === 200) {
          getData()
        }
      } catch (error) {
        console.error(error)
      } finally {
        setLoading(false)
      }
    }
  }, [item, getData])

  return (
    <>
      <Header pageTitle="Rewards" />
      {loading && <Loader />}
      <Card className="p-5 !bg-monochrome-4-60">
        <PageTitle
          title="Rewards"
          subtitle="Invites Downloads"
          className="mb-5"
        />
        <Table
          className="text-nowrap"
          data={invites}
          handleSortBy={handleSortBy}
          handleSearch={handleSearchParams}
          columns={columns}
          columnWidth="20%"
        />
      </Card>
      <Pagination
        size={size}
        totalElements={totalElements}
        setSize={setSize}
        page={page + 1}
        changePage={pageChange}
      />
      <MessageBox
        message={message}
        setShowMessageBox={setShowMessageBox}
        showMessageBox={showMessageBox}
        isError={isError}
      />
      <DeleteConfirmationModal
        showDeleteConfirmationModal={showCancelOrderModal}
        setDeleteConfirmationModal={setShowCancelOrderModal}
        cancelText
        deleteFunc={() => {
          handleCancelOrder()
        }}
        deleteTitle={'order'}
      />
    </>
  )
}
